import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// Lets a page tell the top bar how many records it is showing, when they were last fetched
// and how to fetch them again (for the auto-refresh toggle), without threading props
// through Auth.tsx.
export interface PageMeta {
  total?: number;
  updatedAt?: number;
  refresh?: () => void;
}

interface PageMetaContextValue {
  meta: PageMeta;
  setMeta: (meta: PageMeta) => void;
}

const PageMetaContext = createContext<PageMetaContextValue>({
  meta: {},
  setMeta: () => {},
});

export const PageMetaProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [meta, setMeta] = useState<PageMeta>({});
  const value = useMemo(() => ({ meta, setMeta }), [meta]);
  return (
    <PageMetaContext.Provider value={value}>
      {children}
    </PageMetaContext.Provider>
  );
};

export const usePageMeta = () => useContext(PageMetaContext);

// For list pages: publish the query's total, fetch time and refetch, and clear on unmount.
export const usePublishPageMeta = ({
  total,
  dataUpdatedAt,
  refetch,
}: {
  total?: number;
  dataUpdatedAt: number;
  refetch: () => unknown;
}) => {
  const { setMeta } = usePageMeta();
  useEffect(() => {
    setMeta({ total, updatedAt: dataUpdatedAt || undefined, refresh: refetch });
    return () => setMeta({});
  }, [total, dataUpdatedAt, refetch, setMeta]);
};
